<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FormFooter from './FormFooter.vue';

const emit = defineEmits(['sendMessage', 'cancel']);

const { t } = useI18n();

const title = ref('');
const description = ref('');
const footer = ref('');
const imageUrl = ref('');

const buttonTypeOptions = computed(() => [
  {
    value: 'reply',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_REPLY'),
  },
  {
    value: 'url',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_URL'),
  },
  {
    value: 'call',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_CALL'),
  },
  {
    value: 'copy',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_COPY'),
  },
  {
    value: 'pix',
    label: t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_TYPE_PIX'),
  },
]);

const newButton = () => ({ type: 'reply', displayText: '', value: '' });
const buttons = ref([newButton()]);

const addButton = () => {
  if (buttons.value.length >= 3) return;
  buttons.value.push(newButton());
};
const removeButton = index => {
  if (buttons.value.length <= 1) return;
  buttons.value.splice(index, 1);
};

// Slugifies the label into a stable callback id for reply buttons — the form has no
// dedicated "id" field for type=reply, the label itself is the only thing the agent fills in.
const slugify = text =>
  text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Maps this form's flat `value` field onto the struct's type-specific key
// (id/url/phoneNumber/copyCode) expected by Evolution's Button payload.
const serializeButton = button => {
  const base = { type: button.type, displayText: button.displayText };
  switch (button.type) {
    case 'url':
      return { ...base, id: button.value, url: button.value };
    case 'call':
      return { ...base, phoneNumber: button.value };
    case 'copy':
      return { ...base, id: button.value, copyCode: button.value };
    case 'pix':
      return {
        type: 'pix',
        name: button.displayText,
        key: button.value,
        keyType: 'random',
      };
    default:
      return { ...base, id: slugify(button.displayText) };
  }
};

// type=reply has no visible "value" field (see template) — only the label is required.
const isButtonValid = button => {
  if (button.type === 'reply') return !!button.displayText.trim();
  return !!button.displayText.trim() && !!button.value.trim();
};

const isValid = computed(
  () =>
    title.value.trim() &&
    description.value.trim() &&
    footer.value.trim() &&
    buttons.value.length > 0 &&
    buttons.value.every(isButtonValid)
);

const submit = () => {
  if (!isValid.value) return;

  emit('sendMessage', {
    message: '',
    contentType: 'button',
    contentAttributes: {
      data: {
        title: title.value.trim(),
        description: description.value.trim(),
        footer: footer.value.trim(),
        buttons: buttons.value.map(serializeButton),
        imageUrl: imageUrl.value.trim() || undefined,
      },
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="title"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.TITLE')"
    />
    <TextArea
      v-model="description"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.DESCRIPTION')"
    />
    <Input
      v-model="footer"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.FOOTER')"
    />
    <Input
      v-model="imageUrl"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.IMAGE_URL')"
    />
    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTONS') }}
      </label>
      <p class="text-xs text-n-slate-11 -mt-2">
        {{ $t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.COMBINATION_HINT') }}
      </p>
      <div
        v-for="(button, index) in buttons"
        :key="index"
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
      >
        <div class="flex items-center gap-2">
          <Select
            v-model="button.type"
            :options="buttonTypeOptions"
            class="shrink-0"
          />
          <NextButton
            v-if="buttons.length > 1"
            icon="i-lucide-x"
            ghost
            slate
            sm
            type="button"
            class="ml-auto"
            @click="removeButton(index)"
          />
        </div>
        <Input
          v-model="button.displayText"
          :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_LABEL')"
        />
        <Input
          v-if="button.type === 'url'"
          v-model="button.value"
          :label="
            $t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_VALUE_URL')
          "
        />
        <Input
          v-else-if="button.type === 'call' || button.type === 'pix'"
          v-model="button.value"
          :label="
            $t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_VALUE_PHONE')
          "
        />
        <Input
          v-else-if="button.type === 'copy'"
          v-model="button.value"
          :label="
            $t(
              'CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.BUTTON_VALUE_COPY_CODE'
            )
          "
        />
      </div>
      <NextButton
        v-if="buttons.length < 3"
        icon="i-lucide-plus"
        faded
        slate
        sm
        type="button"
        class="self-start"
        :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.BUTTON.ADD_BUTTON')"
        @click="addButton"
      />
    </div>
    <FormFooter
      :disabled="!isValid"
      @cancel="$emit('cancel')"
      @submit="submit"
    />
  </div>
</template>
